// src/pages/Wallet/hooks/useWalletData.js
import { useState, useEffect, useCallback, useContext } from 'react';
import { WalletContext } from '../../../contexts/WalletContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { AuthContext } from '../../../contexts/AuthContext';
import { ethers, formatEther } from 'ethers';
import { fetchTokens } from '../../../services/tokenFetcher';

// Simple ERC20 ABI for checking balances
const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)'
];

/**
 * Validate a token object to ensure it has the required properties
 * @param {Object} tokenObj - Token object to validate
 * @returns {boolean} - Whether the token is valid
 */
const isValidToken = (tokenObj) => {
  if (!tokenObj || !tokenObj.token) return false;
  
  const token = tokenObj.token;
  
  // ERC-20 tokens should have address and at least a symbol or name
  if (token.type === "ERC-20") {
    return !!token.address && !!(token.symbol || token.name);
  }
  
  return true;
};

/**
 * Hook to manage wallet balance and token data
 * Handles fetching and refreshing of wallet balance and tokens
 */
export const useWalletData = () => {
  const { currentWallet, provider, isInitialized } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  const { isAuthenticated } = useContext(AuthContext);
  
  const [balance, setBalance] = useState('');
  const [tokens, setTokens] = useState([]);
  const [customTokens, setCustomTokens] = useState(() => {
    try {
      const saved = localStorage.getItem('customTokens');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      console.error('Error loading custom tokens from localStorage:', e);
      return {};
    }
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);

  // Refresh native token balance
  const refreshBalance = useCallback(async () => {
    if (!currentWallet || !provider) return;
    try {
      const balBN = await provider.getBalance(currentWallet.address);
      setBalance(formatEther(balBN));
    } catch (err) {
      console.error("Error refreshing balance:", err);
    }
  }, [currentWallet, provider]);

  // Reload custom tokens from localStorage
  const reloadCustomTokens = useCallback(() => {
    try {
      const saved = localStorage.getItem('customTokens');
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Validate custom tokens
        Object.keys(parsed).forEach(chainKey => {
          if (Array.isArray(parsed[chainKey])) {
            parsed[chainKey] = parsed[chainKey].filter(isValidToken);
          }
        });
        
        setCustomTokens(parsed);
        return parsed;
      }
    } catch (e) {
      console.error('Error reloading custom tokens from localStorage:', e);
    }
    return customTokens;
  }, [customTokens]);

  // Refresh custom token balances
  const refreshCustomTokens = useCallback(async () => {
    if (!currentChain || !currentWallet || !provider) return;
    
    try {
      // IMPORTANT: First reload from localStorage to make sure we have any newly added tokens
      const currentCustomTokens = reloadCustomTokens();
      
      const chainKey = currentChain.key;
      const chainTokens = currentCustomTokens[chainKey] || [];
      
      if (chainTokens.length === 0) return;
      
      console.log(`Refreshing ${chainTokens.length} custom tokens for chain ${chainKey}`);
      
      const updatedTokens = await Promise.all(
        chainTokens.map(async (tokenObj) => {
          try {
            // Only process ERC-20 tokens
            if (tokenObj.token && tokenObj.token.type === "ERC-20" && tokenObj.token.address) {
              const contract = new ethers.Contract(
                tokenObj.token.address,
                ERC20_ABI,
                provider
              );
              
              // Get updated balance
              const balanceBN = await contract.balanceOf(currentWallet.address);
              const newValue = balanceBN.toString();
              
              console.log(`Token ${tokenObj.token.symbol || 'unknown'}: Updated balance from ${tokenObj.value || '0'} to ${newValue}`);
              
              // Return updated token object
              return {
                ...tokenObj,
                value: newValue
              };
            }
            return tokenObj;
          } catch (error) {
            console.error(`Error refreshing custom token ${tokenObj.token?.symbol || 'unknown'}:`, error);
            return tokenObj; // Return original on error
          }
        })
      );
      
      // Update custom tokens with new balances
      const updatedCustomTokens = {
        ...currentCustomTokens,
        [chainKey]: updatedTokens
      };
      
      // Important: Set state first
      setCustomTokens(updatedCustomTokens);
      
      // Then save to localStorage with updated balances
      localStorage.setItem('customTokens', JSON.stringify(updatedCustomTokens));
      
      console.log("Custom tokens refreshed and saved to localStorage");
      
    } catch (err) {
      console.error("Error refreshing custom tokens:", err);
    }
  }, [currentChain, currentWallet, provider, reloadCustomTokens]);

  // Refresh token balances
  const refreshTokens = useCallback(async () => {
    if (!currentChain || !currentWallet) return;
    try {
      const fetchedTokens = await fetchTokens(currentChain.key, currentWallet.address);
      
      // Filter out invalid tokens to prevent errors
      const validTokens = fetchedTokens.filter(isValidToken);
      
      // Add missing properties to tokens if needed
      const sanitizedTokens = validTokens.map(tokenObj => {
        if (!tokenObj.token) return tokenObj;
        
        return {
          ...tokenObj,
          token: {
            ...tokenObj.token,
            // Ensure tokens have at least these properties
            name: tokenObj.token.name || tokenObj.token.symbol || 'Unknown Token',
            symbol: tokenObj.token.symbol || 'UNK',
            decimals: tokenObj.token.decimals || 18
          }
        };
      });
      
      setTokens(sanitizedTokens);
      
      // Also refresh custom tokens
      await refreshCustomTokens();
    } catch (err) {
      console.error("Error refreshing tokens:", err);
      setTokens([]);
    }
  }, [currentChain, currentWallet, refreshCustomTokens]);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    // Prevent refreshing too frequently (at least 2 seconds between refreshes)
    const now = Date.now();
    if (now - lastRefreshTime < 2000) {
      return;
    }
    
    setLastRefreshTime(now);
    setIsRefreshing(true);
    
    try {
      console.log("Starting wallet refresh...");
      // Run balance refresh and token refresh in parallel
      await Promise.all([refreshBalance(), refreshTokens()]);
      
      // Explicitly refresh custom tokens again to ensure their balances are updated
      console.log("Explicitly refreshing custom tokens...");
      await refreshCustomTokens();
      
      console.log("Wallet refresh completed");
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      // Add a small delay to show the loading indicator
      setTimeout(() => {
        setIsRefreshing(false);
      }, 800);
    }
  }, [refreshBalance, refreshTokens, refreshCustomTokens, lastRefreshTime]);

  // Initial data load
  useEffect(() => {
    if (isInitialized && isAuthenticated && currentWallet && provider) {
      refreshAll();
    }
  }, [refreshAll, isInitialized, isAuthenticated, currentWallet, provider]);

  // Also refresh when chain changes
  useEffect(() => {
    if (currentChain && currentWallet) {
      refreshAll();
    }
  }, [currentChain, refreshAll, currentWallet]);

  // Listen for localStorage changes from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'customTokens') {
        reloadCustomTokens();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [reloadCustomTokens]);

  return {
    balance,
    tokens,
    customTokens,
    isRefreshing,
    refreshAll,
    refreshBalance,
    refreshTokens,
    refreshCustomTokens,
    reloadCustomTokens
  };
};